/**
 * AB#21537 — how the navigation rail groups the menu.
 *
 * The backend `navigation` list is flat and in no particular order; the rail
 * shows it in three groups a person can predict: the screens they work in
 * every day, the catalogue they maintain, and the setup they touch rarely.
 * An entry the map does not know lands in Catalogue — visible, never lost.
 */
export type NavGroupId = 'work' | 'crm' | 'finance' | 'catalogue' | 'setup';

export const NAV_GROUPS: ReadonlyArray<{ id: NavGroupId; title: string }> = [
  { id: 'work', title: 'Work' },
  // AB#21951 — CRM: customer relationships (Accounts today), behind view-crm.
  { id: 'crm', title: 'CRM' },
  // AB#22166 — Finance: the commission report, behind view-finance.
  { id: 'finance', title: 'Finance' },
  { id: 'catalogue', title: 'Catalogue' },
  { id: 'setup', title: 'Setup' },
];

/** Backend menu names (lower-cased) → group. */
export const GROUP_OF_ENTRY: Readonly<Record<string, NavGroupId>> = {
  dashboard: 'work',
  sales: 'work',
  tracking: 'work',
  'order type': 'work',
  'delivery box': 'work',
  products: 'catalogue',
  'default products': 'catalogue',
  'price list': 'catalogue',
  factory: 'catalogue',
  factories: 'catalogue',
  'design categories': 'catalogue',
  companies: 'catalogue',
  barcode: 'catalogue',
  logos: 'catalogue',
  'acl management': 'setup',
  settings: 'setup',
  'promo video': 'setup',
  'generate drawing sheet': 'setup',
  'invoice portal': 'setup',
};

export const groupIdFor = (name: string | null | undefined): NavGroupId =>
  GROUP_OF_ENTRY[(name || '').trim().toLowerCase()] ?? 'catalogue';

/**
 * Whether the page at `current` is the one `target` links to: the same path,
 * or a page beneath it ("/users/12" is under "/users"). "/" only matches
 * itself, or every entry would be current on the home page.
 */
export const isCurrentPath = (current: string, target: string | null | undefined): boolean => {
  if (!target) return false;
  const t = target.replace(/\/+$/, '') || '/';
  const c = (current || '').replace(/\/+$/, '') || '/';
  if (t === '/') return c === '/';
  return c === t || c.startsWith(`${t}/`);
};
